import { KeyRound, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { DataTable, useDataTable } from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { ApiKey } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { useApiKeyColumns } from '../components/api-key-columns';
import { apiKeyFilters } from '../components/api-key-filters';
import { useApiKeyRowActions, type ApiKeyAction } from '../components/api-key-row-actions';
import { ConfirmApiKeyDialog } from '../components/confirm-api-key-dialog';
import { CreateApiKeyDialog } from '../components/create-api-key-dialog';
import { EditApiKeyDialog } from '../components/edit-api-key-dialog';
import { useApiKeys } from '../hooks/use-api-keys';

const EMPTY: readonly ApiKey[] = [];

function ApiKeys() {
  const { t } = useTranslation('apiKeys');
  const store = useStore();
  const query = useApiKeys(store.id);
  const [creating, setCreating] = useState(false);
  const [target, setTarget] = useState<{ action: ApiKeyAction; key: ApiKey } | null>(null);
  const keys = query.data ?? EMPTY;
  const columns = useApiKeyColumns();
  const filters = useMemo(() => apiKeyFilters(t), [t]);
  const rowActions = useApiKeyRowActions(
    useCallback((action: ApiKeyAction, key: ApiKey) => setTarget({ action, key }), []),
  );
  const table = useDataTable({
    data: keys,
    columns,
    filters,
    getRowId: (row) => row.id,
    initialColumnVisibility: { prefix: false },
  });

  // The route already requires api_keys.manage; only a closed store makes this read-only.
  const { editable, reason } = useStoreEditable({ permission: [] });
  const addButton = (tour?: 'settings.api-keys.create') =>
    editable ? (
      <Button data-tour={tour} onClick={() => setCreating(true)}>
        <Plus aria-hidden />
        {t('create.action')}
      </Button>
    ) : null;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        backTo={{ to: '/org/stores/$storeId', params: { storeId: store.id } }}
        backLabel={t('back')}
        actions={addButton('settings.api-keys.create')}
      />
      <ReadOnlyBanner reason={reason} />
      <Alert
        id={SECTION_ANCHORS.allowedOrigins}
        className="mb-4"
        data-tour="settings.api-keys.intro"
      >
        <KeyRound aria-hidden />
        <AlertDescription>{t('intro')}</AlertDescription>
      </Alert>
      {query.data && keys.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title={t('empty.title')}
          description={t('empty.description')}
          action={addButton()}
          tour="settings.api-keys.empty"
          tourLink
        />
      ) : (
        <DataTable
          table={table}
          search={{ label: t('search'), placeholder: t('search') }}
          rowActions={editable ? rowActions : undefined}
          tour={{ table: 'settings.api-keys.table' }}
          getRowLabel={(row) => row.name}
          resourceName={{ singular: t('resource_one'), plural: t('resource_other') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      )}
      {creating ? <CreateApiKeyDialog storeId={store.id} open onOpenChange={setCreating} /> : null}
      {target?.action === 'edit' ? (
        <EditApiKeyDialog
          key={target.key.id}
          storeId={store.id}
          apiKey={target.key}
          open
          onOpenChange={(open) => {
            if (!open) setTarget(null);
          }}
        />
      ) : null}
      <ConfirmApiKeyDialog
        storeId={store.id}
        target={
          target && target.action !== 'edit' ? { action: target.action, key: target.key } : null
        }
        onOpenChange={(open) => {
          if (!open) setTarget(null);
        }}
      />
    </PageContent>
  );
}

export function ApiKeysPage() {
  return (
    <FeatureErrorBoundary name="apiKeys">
      <ApiKeys />
    </FeatureErrorBoundary>
  );
}
